<script setup>
import { onMounted, ref, reactive, computed } from "vue";

import { apiQuery } from "@/api/j24api";
import AlertsList from "@/components/AlertsList.vue";
import Patch24LicenseEdit from "@/views/elaadmin/Patch24LicenseEdit.vue";

import VSelectSearch from "@/components/VSelectSearch.vue";

const licenses = reactive([]);
const customers = reactive([]);
const loading = ref(false);
const alerts = reactive([]);
const licenseSearch = ref("");
const selectedTab = ref("licenses");
const selectedLicense = ref(null);
const addNewLicense = ref(false);
const sortBy = ref([{ key: "name", order: "asc" }]);

onMounted(() => {
  getData();
});

async function getData() {
  loading.value = true;
  try {
    const result = await apiQuery("ELA", "licences", {
      type: "Patch24",
      apiAction: "GetLicenses",
    });
    if (result.customers) {
      customers.length = 0;
      customers.push(...result.customers);
    }
    if (result.licenses) {
      licenses.length = 0;
      licenses.push(...result.licenses);
    }
    loading.value = false;
  } catch (error) {
    alerts.push({ type: "error", message: "Error loading data: " + error });
    loading.value = false;
  }
}

const licensesProcessed = computed(() => {
  let filtered = licenses.filter((lic) => {
    if (!licenseSearch.value) return true;
    const search = licenseSearch.value.toLowerCase();
    return (
      lic.name?.toLowerCase().includes(search) ||
      lic.customerName?.toLowerCase().includes(search) ||
      lic.status?.toLowerCase().includes(search)
    );
  });
  return filtered.map((lic) => {
    let cust = customers.find((c) => c._id === lic.customerId);
    if (cust) {
      lic.customerName = cust.name;
    } else {
      lic.customerName = "Unknown";
    }
    lic.startdate = toDateObject(lic.startdate);
    lic.enddate = toDateObject(lic.enddate);
    lic.formattedEndDate = formatDate(lic.enddate);
    lic.status = getLicenseStatus(lic.enddate);
    lic.used = lic.usageReport ? lic.usageReport.count : 0;
    return lic;
  });
});

function toDateObject(dateStr) {
  if (!dateStr) return null;
  // If ISO string
  if (!isNaN(Date.parse(dateStr))) {
    return new Date(dateStr);
  }
  // If dd/mm/yyyy
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(dateStr);
  if (match) {
    const [, day, month, year] = match;
    return new Date(`${year}-${month}-${day}T00:00:00`);
  }
  return null;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  // If already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-");
    //return `${day}/${month}/${year}`;
    return `${year}-${month}-${day}`;
  }
  // If ISO date
  const date = new Date(dateStr);
  if (!isNaN(date)) {
    return date.toLocaleDateString();
  }
  return dateStr; // fallback
}
function getLicenseStatus(dateStr) {
  if (!dateStr) return "Expired";
  let date;
  // If YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    date = new Date(dateStr + "T23:59:59Z"); // End of day
  } else {
    date = new Date(dateStr);
  }
  if (isNaN(date)) return "Expired";
  return date >= new Date() ? "Active" : "Expired";
}

const headers = [
  { title: "License Name", value: "name", sortable: true },
  { title: "Customer", value: "customerName", sortable: true },
  { title: "Seats", value: "count", sortable: true },
  { title: "Used", value: "used", sortable: true },
  { title: "Start Date", value: "startdate", sortable: true },
  { title: "Expiry Date", value: "enddate", sortable: true },
  { title: "Status", value: "status", sortable: true },
];

function selectLicense(_, row) {
  selectedLicense.value = row.item;
  selectedTab.value = "details";
}
</script>

<template>
  <AlertsList :alerts="alerts"></AlertsList>
  <v-window v-model="selectedTab">
    <v-window-item value="licenses">
      <v-sheet rounded elevation="1" class="pl-3 pt-3">
        <v-row class="mb-3">
          <v-col cols="auto">
            <v-btn
              prepend-icon="mdi-plus"
              @click="
                addNewLicense = true;
                selectedTab = 'details';
              "
              >Add New License</v-btn
            >
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="4">
            <v-text-field
              v-model="licenseSearch"
              prepend-inner-icon="mdi-magnify"
              placeholder="Search licenses..."
              hide-details
              density="compact"
              variant="outlined"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row
          ><v-col cols="12">
            <v-data-table
              :headers="headers"
              :items="licensesProcessed"
              :loading="loading"
              item-key="_id"
              v-model:sort-by="sortBy"
              @click:row="selectLicense"
              must-sort
            >
              <template v-slot:[`item.startdate`]="{ item }">
                {{ formatDate(item.startdate) }}
              </template>

              <template v-slot:[`item.enddate`]="{ item }">
                {{ formatDate(item.enddate) }}
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-sheet></v-window-item
    >
    <v-window-item value="details">
      <Patch24LicenseEdit
        :license="selectedLicense"
        :customers="customers"
        :newLicense="addNewLicense"
        :licenses="licenses"
        @back="
          selectedTab = 'licenses';
          addNewLicense = false;
          selectedLicense = {};
        "
        @updated="getData()"
      />
    </v-window-item>
  </v-window>
</template>
